import { useEffect, useState } from "react";
import { Star, Flag, BarChart3, Settings as SettingsIcon, Shield, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Stars, RatingDistribution } from "@/components/reviews/Stars";
import { toast } from "sonner";

const STATUS_ACTIONS = [["approve", "Publish"], ["hide", "Hide"], ["reject", "Reject"], ["remove", "Remove"], ["flag", "Flag"], ["restore", "Restore"]];

export default function AdminReviews() {
  const [tab, setTab] = useState("reviews");
  return (
    <div data-testid="admin-reviews-page">
      <PageHeader title="Reviews & Ratings" icon={Star} subtitle="Moderate customer reviews, handle reports, monitor reputation and configure the review engine." />
      <div className="mb-4 flex flex-wrap gap-2">
        {[["reviews", "Reviews", Star], ["reports", "Reports", Flag], ["analytics", "Analytics", BarChart3], ["settings", "Settings", SettingsIcon]].map(([v, label, Icon]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ${tab === v ? "brand-gradient-bg text-white" : "border border-slate-200 text-slate-600"}`}><Icon className="h-4 w-4" /> {label}</button>
        ))}
      </div>
      {tab === "reviews" && <ReviewsTab />}
      {tab === "reports" && <ReportsTab />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function ReviewsTab() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [reported, setReported] = useState(false);

  const load = () => api.get("/reviews/admin/list", { params: { q: q || undefined, status, reported } }).then((r) => setData(r.data)).catch(() => setData({ reviews: [], total: 0, statuses: [] }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, reported]);

  const moderate = async (id, action) => {
    try { await api.post(`/reviews/admin/${id}/moderate`, { action }); toast.success(`Review ${action}d`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const recheck = async (id) => {
    try { await api.post(`/reviews/admin/${id}/ai-recheck`); toast.success("AI re-checked"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search text/name…" data-testid="admin-review-search" className="h-9 w-56 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-blue-400" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-review-status" className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600">
          <option value="all">All statuses</option>
          {(data?.statuses || ["published", "pending_moderation", "hidden", "flagged", "rejected", "removed"]).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setReported((v) => !v)} data-testid="admin-review-reported" className={`rounded-lg border px-3 py-1.5 text-sm ${reported ? "border-rose-300 bg-rose-50 text-rose-600" : "border-slate-200 text-slate-600"}`}>Reported only</button>
      </div>
      <p className="mb-2 text-xs text-slate-400">{data ? `${data.total} reviews` : ""}</p>
      <div className="space-y-3" data-testid="admin-reviews-list">
        {data?.reviews?.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">No reviews found.</p>}
        {(data?.reviews || []).map((r) => (
          <div key={r.review_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-review-${r.review_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><Stars value={r.rating} size={13} /><span className="text-sm font-semibold text-slate-800">{r.display_name}</span><span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium capitalize text-slate-500">{r.status}</span>{r.reported_count > 0 && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-600">{r.reported_count} report(s)</span>}</div>
                <p className="text-xs text-slate-400">{r.product_name} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}{r.verified_purchase ? " · Verified Purchase" : ""}</p>
                {r.title && <p className="mt-1 text-sm font-medium text-slate-700">{r.title}</p>}
                {r.body && <p className="text-sm text-slate-600">{r.body}</p>}
                {r.ai && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`ai-flags-${r.review_id}`}>
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600">AI</span>
                    {r.ai.priority && <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${r.ai.priority === "high" ? "bg-rose-100 text-rose-600" : r.ai.priority === "medium" ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500"}`}>priority: {r.ai.priority}</span>}
                    {r.ai.sentiment && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">{r.ai.sentiment}</span>}
                    {r.ai.spam && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] text-rose-600">spam?</span>}
                    {r.ai.abusive && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] text-rose-600">abusive?</span>}
                    {r.ai.likely_fake && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] text-rose-600">likely fake?</span>}
                    {r.ai.policy_violation && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] text-rose-600">policy?</span>}
                    {r.ai.reasons && <span className="text-[11px] text-slate-400">— {r.ai.reasons}</span>}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {STATUS_ACTIONS.map(([a, label]) => (
                <button key={a} onClick={() => moderate(r.review_id, a)} data-testid={`moderate-${a}-${r.review_id}`} className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600">{label}</button>
              ))}
              <button onClick={() => recheck(r.review_id)} data-testid={`ai-recheck-${r.review_id}`} className="rounded-full border border-indigo-200 px-2.5 py-1 text-[11px] font-medium text-indigo-600 hover:bg-indigo-50">AI re-check</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsTab() {
  const [reports, setReports] = useState(null);
  const load = () => api.get("/reviews/admin/reports/list").then((r) => setReports(r.data.reports)).catch(() => setReports([]));
  useEffect(() => { load(); }, []);
  const resolve = async (id, s) => {
    try { await api.post(`/reviews/admin/reports/${id}/resolve`, { status: s }); toast.success(`Report ${s}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="admin-reports-list">
      {reports?.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">No reports.</p>}
      {(reports || []).map((rep) => (
        <div key={rep.report_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-report-${rep.report_id}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800">{rep.reason} <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] capitalize text-slate-500">{rep.status}</span></p>
              <p className="text-xs text-slate-400">By {rep.reporter_role} · {rep.created_at ? new Date(rep.created_at).toLocaleDateString() : ""}</p>
              {rep.detail && <p className="mt-1 text-sm text-slate-600">{rep.detail}</p>}
              {rep.review && <p className="mt-1 rounded-lg bg-slate-50 p-2 text-sm text-slate-600">“{rep.review.title || rep.review.body || "—"}” · {rep.review.rating}★ · {rep.review.status}</p>}
            </div>
            <div className="flex shrink-0 gap-1.5">
              <button onClick={() => resolve(rep.report_id, "resolved")} data-testid={`report-resolve-${rep.report_id}`} className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-600">Resolve</button>
              <button onClick={() => resolve(rep.report_id, "dismissed")} data-testid={`report-dismiss-${rep.report_id}`} className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-500">Dismiss</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsTab() {
  const [a, setA] = useState(null);
  const [bomb, setBomb] = useState(null);
  useEffect(() => {
    api.get("/reviews/admin/analytics/summary").then((r) => setA(r.data)).catch(() => setA(null));
    api.get("/reviews/admin/bombing/watch").then((r) => setBomb(r.data)).catch(() => setBomb(null));
  }, []);
  if (!a) return <p className="text-sm text-slate-500">Loading…</p>;
  const cards = [["Total", a.total], ["Published", a.published], ["Pending", a.pending], ["Flagged", a.flagged], ["Removed/Rejected", a.removed], ["Reported", a.reported], ["Open reports", a.open_reports], ["Marketplace avg", a.marketplace_avg ?? "—"]];
  return (
    <div data-testid="admin-analytics">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map(([label, val]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs uppercase tracking-wide text-slate-400">{label}</p><p className="mt-1 text-2xl font-extrabold text-slate-900">{val}</p></div>
        ))}
      </div>
      <div className="mt-4 max-w-md rounded-2xl border border-slate-200 bg-white p-5">
        <p className="mb-2 text-sm font-semibold text-slate-700">Marketplace rating distribution</p>
        <RatingDistribution dist={a.rating_dist} total={a.published} />
      </div>
      <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/40 p-5" data-testid="review-bombing-watch">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700"><Flag className="h-4 w-4" /> Review-bombing watch <span className="text-xs font-normal text-amber-600">(last {bomb?.window_hours || 72}h, ≥{bomb?.threshold || 3} low ratings — nothing is auto-hidden)</span></p>
        {(!bomb || bomb.flagged.length === 0) ? (
          <p className="mt-2 text-sm text-slate-500">No suspicious low-rating bursts detected.</p>
        ) : (
          <div className="mt-2 space-y-1.5">
            {bomb.flagged.map((f) => (
              <div key={f.product_id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm" data-testid={`bombing-${f.product_id}`}>
                <span className="min-w-0 truncate font-medium text-slate-700">{f.product_name || f.product_id}</span>
                <span className="shrink-0 text-xs text-slate-500"><b className="text-rose-600">{f.low_recent}</b> low / {f.total_reviews} total</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/reviews/admin/settings/get").then((r) => setS(r.data.settings)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/reviews/admin/settings/update", s); setS(data.settings); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!s) return <p className="text-sm text-slate-500">Loading…</p>;
  const num = (k, label) => (
    <div><label className="mb-1 block text-xs font-medium text-slate-500">{label}</label><input type="number" value={s[k]} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} data-testid={`setting-${k}`} className="h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-blue-400" /></div>
  );
  const bool = (k, label) => (
    <label className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm text-slate-600"><span>{label}</span><input type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} data-testid={`setting-${k}`} /></label>
  );
  return (
    <div className="max-w-2xl rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-settings">
      <div className="grid gap-3 sm:grid-cols-2">
        {num("edit_window_hours", "Edit window (hours)")}
        {num("max_images", "Max images per review")}
        {num("max_image_mb", "Max image size (MB)")}
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Moderation mode</label>
          <select value={s.moderation_mode} onChange={(e) => setS({ ...s, moderation_mode: e.target.value })} data-testid="setting-moderation_mode" className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600">
            <option value="post">Auto-publish then moderate</option>
            <option value="pre">Hold for moderation before publish</option>
          </select>
        </div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">AI moderation sensitivity</label>
          <select value={s.ai_sensitivity} onChange={(e) => setS({ ...s, ai_sensitivity: e.target.value })} data-testid="setting-ai_sensitivity" className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-600">
            <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
          </select>
        </div>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {bool("allow_rating_change", "Allow rating change on edit")}
        {bool("allow_image_change", "Allow image change on edit")}
        {bool("keep_seller_response_after_edit", "Keep seller response after edit")}
        {bool("helpful_voting", "Enable helpful voting")}
        {bool("multiple_per_product", "Allow multiple reviews per product")}
        {bool("invite_on_delivery", "Send review invitation on delivery")}
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-settings-save" className="mt-4 inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save settings</button>
    </div>
  );
}
